import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Tag } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Link } from "react-router-dom";
import { Plus, Send, FileText, CheckCircle2, Eye, AlertTriangle, Check, X, Receipt } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import usePermissions from "@/lib/usePermissions";
import DriveLinkPreviewModal from "@/components/DriveLinkPreviewModal";

const QUOTE_STATUS_TONE = { awaiting_approval: "yellow", approved: "green", rejected: "red" };
const PO_STATUS_TONE = { draft: "slate", sent: "blue", acknowledged: "yellow", invoice_uploaded: "orange", closed: "green", cancelled: "red" };

const blankQuote = () => ({
  vendor_id: "", project_id: "", service_type: "", amount: "", currency: "INR",
  proforma_file_url: "", proforma_file_name: "", notes: "",
});
const blankLine = () => ({ _uid: Math.random().toString(36).slice(2, 9), description: "", qty: 1, unit_price: 0, currency: "INR" });
const blankPo = () => ({ vendor_id: "", project_id: "", title: "", payment_terms: "Net 30", delivery_terms: "", notes: "", line_items: [blankLine()] });
const blankInvoice = () => ({ invoice_number: "", invoice_file_url: "", invoice_file_name: "", amount: "", note: "" });

export default function VendorProcurement() {
  const { user } = useAuth();
  const can = usePermissions();
  // Phase 114 — Founder-only, deliberately excludes Admin (see backend _can_decide).
  const isFounder = user?.role === "founder";
  const canAddInvoice = can("purchases_edit");
  const canCreatePo = can("purchases_create");

  const [quotes, setQuotes] = useState([]);
  const [pos, setPos] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [projects, setProjects] = useState([]);

  const load = useCallback(async () => {
    try {
      const [{ data: q }, { data: p }, { data: v }, { data: proj }] = await Promise.all([
        api.get("/procurement/quotes"), api.get("/admin/pos").catch(() => ({ data: [] })),
        api.get("/procurement/vendor-options"), api.get("/projects"),
      ]);
      setQuotes(q); setPos(p); setVendors(v); setProjects(proj);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // ---- Quote decision (Founder-only) --------------------------------------
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectNote, setRejectNote] = useState("");
  const decideQuote = async (id, decision, note = "") => {
    try {
      await api.post(`/approvals/quote/${id}/decide`, { decision, note: note || undefined });
      toast.success(decision === "approve" ? "Quote approved · PO emailed to vendor" : "Quote rejected");
      setRejectTarget(null); setRejectNote("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // ---- Add Vendor Invoice (Phase 3) ---------------------------------------
  const [invoicePo, setInvoicePo] = useState(null);
  const [invoiceForm, setInvoiceForm] = useState(blankInvoice());
  const [docPreview, setDocPreview] = useState(null); // { url, title }
  const invoiceVariancePct = invoicePo && invoiceForm.amount && Number(invoicePo.total) > 0
    ? Math.round(((Number(invoiceForm.amount) - Number(invoicePo.total)) / Number(invoicePo.total)) * 10000) / 100
    : 0;
  const submitInvoice = async () => {
    if (!invoiceForm.invoice_number.trim()) { toast.error("Invoice number is required"); return; }
    if (!invoiceForm.invoice_file_url.trim()) { toast.error("Paste the vendor's invoice Google Drive link"); return; }
    if (!invoiceForm.amount || Number(invoiceForm.amount) <= 0) { toast.error("Enter the final invoice amount"); return; }
    try {
      const { data } = await api.post(`/admin/pos/${invoicePo.id}/invoice`, {
        invoice_number: invoiceForm.invoice_number, invoice_file_url: invoiceForm.invoice_file_url,
        invoice_file_name: "Google Drive link", amount: Number(invoiceForm.amount), note: invoiceForm.note,
      });
      toast.success("Invoice matched · posted to Expenses as Pending Payment");
      if (data?._warning) toast.warning(data._warning, { duration: 8000 });
      setInvoicePo(null); setInvoiceForm(blankInvoice()); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // ---- Add Quote (Phase 1) -------------------------------------------------
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteForm, setQuoteForm] = useState(blankQuote());
  const submitQuote = async () => {
    if (!quoteForm.vendor_id) { toast.error("Pick a vendor from the Registry"); return; }
    if (!quoteForm.service_type.trim()) { toast.error("Service type is required"); return; }
    if (!quoteForm.amount || Number(quoteForm.amount) <= 0) { toast.error("Enter a quote amount"); return; }
    if (!quoteForm.proforma_file_url.trim()) { toast.error("Paste the proforma invoice's Google Drive link"); return; }
    try {
      await api.post("/procurement/quotes", {
        ...quoteForm, project_id: quoteForm.project_id || null, amount: Number(quoteForm.amount), proforma_file_name: "Google Drive link",
      });
      toast.success("Quote submitted · Awaiting Founder approval");
      setQuoteOpen(false); setQuoteForm(blankQuote()); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // ---- New PO (manual, unchanged from earlier phase) -----------------------
  const [poOpen, setPoOpen] = useState(false);
  const [poForm, setPoForm] = useState(blankPo());
  const [poSaving, setPoSaving] = useState(false);
  const setLine = (i, k, v) => setPoForm((f) => ({ ...f, line_items: f.line_items.map((li, j) => j === i ? { ...li, [k]: v } : li) }));
  const addLine = () => setPoForm((f) => ({ ...f, line_items: [...f.line_items, blankLine()] }));
  const removeLine = (i) => setPoForm((f) => ({ ...f, line_items: f.line_items.filter((_, j) => j !== i) }));
  const poTotal = (poForm.line_items || []).reduce((s, li) => s + Number(li.qty || 0) * Number(li.unit_price || 0), 0);
  const submitPo = async (sendNow) => {
    if (!poForm.vendor_id) { toast.error("Pick a vendor"); return; }
    if (!poForm.title.trim()) { toast.error("Title is required"); return; }
    setPoSaving(true);
    try {
      const payload = {
        vendor_id: poForm.vendor_id, project_id: poForm.project_id || null, title: poForm.title,
        payment_terms: poForm.payment_terms, delivery_terms: poForm.delivery_terms, notes: poForm.notes,
        line_items: poForm.line_items.map((li) => ({ description: li.description, qty: Number(li.qty), unit_price: Number(li.unit_price), currency: li.currency })),
      };
      const { data: created } = await api.post("/admin/pos", payload);
      if (sendNow) { await api.post(`/admin/pos/${created.id}/send`); toast.success(`PO ${created.po_number} sent to vendor`); }
      else toast.success(`Draft PO ${created.po_number} saved`);
      setPoOpen(false); setPoForm(blankPo()); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPoSaving(false); }
  };
  const sendDraft = async (po) => {
    if (!window.confirm(`Send ${po.po_number} to ${po.vendor_name}?`)) return;
    try { await api.post(`/admin/pos/${po.id}/send`); toast.success("Sent"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const [active, setActive] = useState(null);

  return (
    <div data-testid="vendor-procurement">
      {/* ---- Quotes ---- */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-display font-bold text-sm text-slate-900">Vendor quotes</h3>
          <p className="text-xs text-slate-500">Raise a quote, get Founder approval, and the PO is emailed to the vendor automatically.</p>
        </div>
        <Button onClick={() => setQuoteOpen(true)} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="new-quote-btn">
          <Plus size={14} className="mr-1.5" />Add quote
        </Button>
      </div>

      {quotes.length === 0 ? (
        <div className="card-flat p-6 text-center text-sm text-slate-500 mb-8" data-testid="quotes-empty">No quotes yet.</div>
      ) : (
        <div className="space-y-2 mb-8">
          {quotes.map((q) => (
            <div key={q.id} className="card-flat p-4" data-testid={`quote-${q.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-display font-bold text-sm text-slate-900">{q.service_type}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">{q.vendor_name} · {q.project_name || "No project"} · by {q.requested_by_name}</div>
                </div>
                <div className="text-right shrink-0">
                  <Tag tone={QUOTE_STATUS_TONE[q.status] || "slate"} data-testid={`quote-status-${q.id}`}>{q.status.replace("_", " ")}</Tag>
                  <div className="font-mono font-bold text-sm mt-1">{q.currency} {Number(q.amount || 0).toLocaleString("en-IN")}</div>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-3">
                {q.proforma_file_url && (
                  <a href={q.proforma_file_url} target="_blank" rel="noreferrer" className="text-[11px] text-[#F54800] hover:underline inline-flex items-center gap-1" data-testid={`quote-pi-${q.id}`}>
                    <FileText size={11} />Proforma invoice
                  </a>
                )}
                {q.status === "awaiting_approval" && isFounder && (
                  <div className="inline-flex gap-1.5 ml-auto">
                    <Button size="sm" onClick={() => decideQuote(q.id, "approve")} className="rounded-sm h-7 bg-green-600 hover:bg-green-700 text-white" data-testid={`quote-approve-${q.id}`}>
                      <Check size={12} className="mr-1" />Approve
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setRejectTarget(q)} className="rounded-sm h-7 border-red-200 text-red-600 hover:bg-red-50" data-testid={`quote-reject-${q.id}`}>
                      <X size={12} className="mr-1" />Reject
                    </Button>
                  </div>
                )}
                {q.status === "awaiting_approval" && !isFounder && (
                  <span className="text-[11px] text-slate-400 ml-auto">Awaiting Founder approval</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ---- Purchase Orders ---- */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-display font-bold text-sm text-slate-900">Purchase orders</h3>
          <p className="text-xs text-slate-500">Match the final vendor invoice to post it to Expenses as a Pending Payment.</p>
        </div>
        {canCreatePo && (
          <Button variant="outline" onClick={() => setPoOpen(true)} className="rounded-sm" data-testid="new-po-btn">
            <Plus size={14} className="mr-1.5" />New PO
          </Button>
        )}
      </div>

      {pos.length === 0 ? (
        <div className="card-flat p-6 text-center text-sm text-slate-500" data-testid="pos-empty">No purchase orders yet.</div>
      ) : (
        <div className="space-y-2">
          {pos.map((po) => (
            <div key={po.id} className="card-flat p-4" data-testid={`po-${po.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-[10px] text-slate-400 uppercase">{po.po_number}</div>
                  <div className="font-display font-bold text-sm text-slate-900">{po.title}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">{po.vendor_name} · {(po.line_items || []).length} line(s)</div>
                </div>
                <div className="text-right shrink-0">
                  <Tag tone={PO_STATUS_TONE[po.status] || "slate"} data-testid={`po-status-${po.id}`}>{po.status?.replace("_", " ")}</Tag>
                  <div className="font-mono font-bold text-sm mt-1">{po.currency} {Number(po.total || 0).toLocaleString("en-IN")}</div>
                </div>
              </div>
              <div className="flex items-center gap-1.5 mt-3">
                {po.status === "draft" && canCreatePo && <Button size="sm" onClick={() => sendDraft(po)} className="rounded-sm bg-slate-900 hover:bg-slate-700 text-white" data-testid={`po-send-${po.id}`}><Send size={11} className="mr-1" />Send</Button>}
                {(po.status === "sent" || po.status === "acknowledged") && canAddInvoice && (
                  <Button size="sm" onClick={() => { setInvoicePo(po); setInvoiceForm(blankInvoice()); }} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid={`po-add-invoice-${po.id}`}>
                    <Receipt size={11} className="mr-1" />Add vendor invoice
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => setActive(po)} className="rounded-sm"><FileText size={11} className="mr-1" />View</Button>
                {po.status === "invoice_uploaded" && <span className="text-[11px] text-orange-700 ml-1 inline-flex items-center gap-1"><CheckCircle2 size={11} />In Pending Payments</span>}
                {po.status === "closed" && <span className="text-[11px] text-green-700 ml-1 inline-flex items-center gap-1"><CheckCircle2 size={11} />Paid</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ---- Add Quote dialog ---- */}
      <Dialog open={quoteOpen} onOpenChange={setQuoteOpen}>
        <DialogContent className="rounded-sm max-w-lg max-h-[92vh] overflow-y-auto" data-testid="add-quote-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Add quote</DialogTitle>
            <DialogDescription>Every field flows into the Purchase Order once a Founder approves this quote.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="label-overline">Vendor *</Label>
              <Select value={quoteForm.vendor_id} onValueChange={(v) => setQuoteForm({ ...quoteForm, vendor_id: v })}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="quote-vendor"><SelectValue placeholder="Pick a registered vendor…" /></SelectTrigger>
                <SelectContent>
                  {vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.company || v.name} · {v.category || "—"}</SelectItem>)}
                </SelectContent>
              </Select>
              {vendors.length === 0 && (
                <p className="text-[11px] text-red-600 mt-1.5 inline-flex items-center gap-1" data-testid="quote-vendor-warning">
                  <AlertTriangle size={11} />Please register this vendor in the <Link to="/studio?tab=finance&sub=vendors" className="underline">Registry</Link> before adding a quote.
                </p>
              )}
            </div>
            <div>
              <Label className="label-overline">Project</Label>
              <Select value={quoteForm.project_id || "__none"} onValueChange={(v) => setQuoteForm({ ...quoteForm, project_id: v === "__none" ? "" : v })}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="quote-project"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">— None —</SelectItem>
                  {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-overline">Service type *</Label>
              <Input className="rounded-sm mt-1" value={quoteForm.service_type} onChange={(e) => setQuoteForm({ ...quoteForm, service_type: e.target.value })} placeholder="e.g. Injection moulding tooling" data-testid="quote-service-type" />
            </div>
            <div>
              <Label className="label-overline">Quote amount ₹ *</Label>
              <Input type="number" min="0" className="rounded-sm mt-1" value={quoteForm.amount} onChange={(e) => setQuoteForm({ ...quoteForm, amount: e.target.value })} data-testid="quote-amount" />
            </div>
            <div className="border border-slate-200 rounded-sm p-3 bg-slate-50/60">
              <Label className="label-overline">Proforma invoice — Google Drive link *</Label>
              <div className="flex items-center gap-1.5 mt-1.5">
                <Input className="rounded-sm" value={quoteForm.proforma_file_url} onChange={(e) => setQuoteForm({ ...quoteForm, proforma_file_url: e.target.value })} placeholder="https://drive.google.com/file/d/…" data-testid="quote-drive-link-input" />
                {quoteForm.proforma_file_url && (
                  <button type="button" onClick={() => setDocPreview({ url: quoteForm.proforma_file_url, title: "Proforma invoice" })} className="shrink-0 text-slate-400 hover:text-[#F54800]" data-testid="quote-drive-link-preview"><Eye size={15} /></button>
                )}
              </div>
            </div>
            <div>
              <Label className="label-overline">Notes</Label>
              <Textarea rows={2} className="rounded-sm mt-1 resize-none" value={quoteForm.notes} onChange={(e) => setQuoteForm({ ...quoteForm, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={submitQuote} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="submit-quote">Submit for approval</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---- Reject quote dialog (mandatory reason) ---- */}
      <Dialog open={!!rejectTarget} onOpenChange={(o) => !o && setRejectTarget(null)}>
        <DialogContent className="rounded-sm max-w-md" data-testid="reject-quote-dialog">
          <DialogHeader><DialogTitle className="font-display">Reject quote</DialogTitle></DialogHeader>
          <Textarea rows={3} className="rounded-sm" placeholder="Reason (required)…" value={rejectNote} onChange={(e) => setRejectNote(e.target.value)} data-testid="reject-quote-reason" />
          <DialogFooter>
            <Button onClick={() => decideQuote(rejectTarget.id, "reject", rejectNote)} disabled={!rejectNote.trim()} className="rounded-sm bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-reject-quote">Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---- Add Vendor Invoice dialog (Phase 3) ---- */}
      <Dialog open={!!invoicePo} onOpenChange={(o) => !o && setInvoicePo(null)}>
        <DialogContent className="rounded-sm max-w-md" data-testid="add-invoice-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Add vendor invoice</DialogTitle>
            <DialogDescription>{invoicePo && `Matching to ${invoicePo.po_number} · Approved total ${invoicePo.currency} ${Number(invoicePo.total).toLocaleString("en-IN")}`}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="label-overline">Invoice number *</Label>
              <Input className="rounded-sm mt-1" value={invoiceForm.invoice_number} onChange={(e) => setInvoiceForm({ ...invoiceForm, invoice_number: e.target.value })} data-testid="invoice-number" />
            </div>
            <div>
              <Label className="label-overline">Final invoice amount *</Label>
              <Input type="number" min="0" className="rounded-sm mt-1" value={invoiceForm.amount} onChange={(e) => setInvoiceForm({ ...invoiceForm, amount: e.target.value })} data-testid="invoice-amount" />
              {invoiceVariancePct > 5 && (
                <p className="text-[11px] text-red-600 mt-1.5 inline-flex items-center gap-1" data-testid="invoice-variance-warning">
                  <AlertTriangle size={11} />{invoiceVariancePct}% higher than the approved PO total.
                </p>
              )}
            </div>
            <div className="border border-slate-200 rounded-sm p-3 bg-slate-50/60">
              <Label className="label-overline">Invoice — Google Drive link *</Label>
              <div className="flex items-center gap-1.5 mt-1.5">
                <Input className="rounded-sm" value={invoiceForm.invoice_file_url} onChange={(e) => setInvoiceForm({ ...invoiceForm, invoice_file_url: e.target.value })} placeholder="https://drive.google.com/file/d/…" data-testid="invoice-drive-link-input" />
                {invoiceForm.invoice_file_url && (
                  <button type="button" onClick={() => setDocPreview({ url: invoiceForm.invoice_file_url, title: "Vendor invoice" })} className="shrink-0 text-slate-400 hover:text-[#F54800]" data-testid="invoice-drive-link-preview"><Eye size={15} /></button>
                )}
              </div>
            </div>
            <div>
              <Label className="label-overline">Note</Label>
              <Textarea rows={2} className="rounded-sm mt-1 resize-none" value={invoiceForm.note} onChange={(e) => setInvoiceForm({ ...invoiceForm, note: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={submitInvoice} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="submit-invoice">Match to PO</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---- New PO dialog (manual) ---- */}
      <Dialog open={poOpen} onOpenChange={setPoOpen}>
        <DialogContent className="rounded-sm max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="new-po-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">New purchase order</DialogTitle>
            <DialogDescription>For a PO issued directly, without going through a Quote first.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="label-overline">Vendor *</Label>
                <Select value={poForm.vendor_id} onValueChange={(v) => setPoForm({ ...poForm, vendor_id: v })}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="po-vendor"><SelectValue placeholder="Pick a vendor…" /></SelectTrigger>
                  <SelectContent>{vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.company || v.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-overline">Project</Label>
                <Select value={poForm.project_id || "__none"} onValueChange={(v) => setPoForm({ ...poForm, project_id: v === "__none" ? "" : v })}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="po-project"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent><SelectItem value="__none">— None —</SelectItem>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2"><Label className="label-overline">Title *</Label><Input className="rounded-sm mt-1" value={poForm.title} onChange={(e) => setPoForm({ ...poForm, title: e.target.value })} data-testid="po-title" /></div>
              <div><Label className="label-overline">Payment terms</Label><Input className="rounded-sm mt-1" value={poForm.payment_terms} onChange={(e) => setPoForm({ ...poForm, payment_terms: e.target.value })} /></div>
              <div><Label className="label-overline">Delivery</Label><Input className="rounded-sm mt-1" value={poForm.delivery_terms} onChange={(e) => setPoForm({ ...poForm, delivery_terms: e.target.value })} /></div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="label-overline">Line items</Label>
                <Button size="sm" variant="outline" onClick={addLine} className="rounded-sm h-7" data-testid="po-add-line"><Plus size={11} className="mr-1" />Add line</Button>
              </div>
              <div className="space-y-2">
                {poForm.line_items.map((li, i) => (
                  <div key={li._uid || i} className="grid grid-cols-12 gap-2 items-end" data-testid={`po-line-${i}`}>
                    <div className="col-span-5"><Input className="rounded-sm" placeholder="Description" value={li.description} onChange={(e) => setLine(i, "description", e.target.value)} data-testid={`po-line-desc-${i}`} /></div>
                    <div className="col-span-2"><Input type="number" className="rounded-sm" placeholder="Qty" value={li.qty} onChange={(e) => setLine(i, "qty", e.target.value)} data-testid={`po-line-qty-${i}`} /></div>
                    <div className="col-span-2"><Input type="number" className="rounded-sm" placeholder="Unit" value={li.unit_price} onChange={(e) => setLine(i, "unit_price", e.target.value)} data-testid={`po-line-price-${i}`} /></div>
                    <div className="col-span-2 font-mono text-xs text-right">{(Number(li.qty || 0) * Number(li.unit_price || 0)).toFixed(2)}</div>
                    <div className="col-span-1"><button onClick={() => removeLine(i)} disabled={poForm.line_items.length <= 1} className="text-slate-400 hover:text-red-600 disabled:opacity-30"><X size={13} /></button></div>
                  </div>
                ))}
              </div>
              <div className="text-right text-xs font-mono font-bold mt-2 pt-2 border-t border-slate-200">Total · INR {poTotal.toFixed(2)}</div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => submitPo(false)} disabled={poSaving} className="rounded-sm" data-testid="po-save-draft">Save as draft</Button>
            <Button onClick={() => submitPo(true)} disabled={poSaving} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="po-save-and-send"><Send size={13} className="mr-1.5" />Save & send</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---- PO detail dialog ---- */}
      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="rounded-sm max-w-2xl">
          {active && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display">{active.po_number} · {active.title}</DialogTitle>
                <DialogDescription>{active.vendor_name} · Total {active.currency} {Number(active.total || 0).toLocaleString("en-IN")}</DialogDescription>
              </DialogHeader>
              <div className="space-y-2 text-xs">
                {(active.line_items || []).map((li, i) => (
                  <div key={`po-detail-line-${i}`} className="flex items-center justify-between border-b border-slate-100 py-1">
                    <span>{li.description}</span>
                    <span className="font-mono">{li.qty} × {li.unit_price} = {(li.qty * li.unit_price).toFixed(2)}</span>
                  </div>
                ))}
                {active.invoice && (
                  <div className="card-flat p-3 mt-3 bg-orange-50/30">
                    <div className="font-semibold">Vendor invoice: {active.invoice.number} · {active.currency} {Number(active.invoice.amount || 0).toLocaleString("en-IN")}</div>
                    {active.invoice.variance_pct > 5 && <div className="text-red-600 mt-1 inline-flex items-center gap-1"><AlertTriangle size={11} />{active.invoice.variance_pct}% over PO total</div>}
                    {active.invoice.file_url && <div><a href={active.invoice.file_url} target="_blank" rel="noreferrer" className="text-[#F54800] hover:underline">Open invoice →</a></div>}
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      <DriveLinkPreviewModal open={!!docPreview} url={docPreview?.url} title={docPreview?.title} onClose={() => setDocPreview(null)} />
    </div>
  );
}
